/**
 * API client.
 *
 * The dashboard is fetched with ONE request per screen — that is the design
 * decision that makes it feel instant, not the database. Twelve widgets pulled
 * separately would be twelve round trips.
 *
 * Auth: when the app is opened from inside Bitrix24 the server hands the session
 * token to the page in `window.__ZA_BOOT__` and also sets a cookie. Browsers that
 * block third-party cookies in an iframe still work, because we fall back to the
 * bootstrap token as an Authorization header.
 */

declare global {
  interface Window {
    __ZA_BOOT__?: { token: string | null; error: string | null };
  }
}

const STORAGE_KEY = "za_token";

export function bootToken(): string | null {
  const fromBoot = window.__ZA_BOOT__?.token ?? null;
  if (fromBoot) {
    try {
      sessionStorage.setItem(STORAGE_KEY, fromBoot);
    } catch {
      /* приватный режим — обойдёмся памятью */
    }
    return fromBoot;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* ignore */
  }
}

/**
 * Ошибка запроса вместе с РАЗОБРАННЫМ телом ответа.
 *
 * До 10.09.2026 сюда попадали только код HTTP и фраза, а остальные поля тела
 * выбрасывались. Из-за этого не работал перевод отказов: сервер отдаёт вместе с
 * фразой машинный `code` (и `expected` там, где отказ про вид значения), чтобы
 * интерфейс напечатал объяснение на своём языке, — а до интерфейса код не
 * доходил, и на экране Administration печаталась русская фраза сервера. Ровно
 * тот дефект, ради которого код и вводили.
 *
 * Тело лежит целиком, а не разобранное по известным полям: список полей у
 * разных отказов разный, и перечислять их здесь значило бы править этот класс
 * при каждом новом виде отказа.
 */
export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;
  /** Машинный код отказа, если сервер его прислал. */
  code: string | null;

  constructor(status: number, message: string, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = typeof body.code === "string" ? body.code : null;
  }
}

/** Экспорт — для модулей экранов (`lib/b2bApi.ts`): один запрос, одна обработка ошибок. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = bootToken();
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
      if (typeof body.error === "string" && body.error !== "") msg = body.error;
    } catch {
      /* тело не JSON — оставляем код */
    }
    throw new ApiError(res.status, msg, body);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------- типы

export type Park = {
  park_key: string;
  name: string;
  brand_key: string;
  currency: string;
  timezone: string;
  active: boolean;
};

export type ParkGeo = {
  park_key: string;
  name: string;
  brand_key: string;
  brand_name: string;
  country: string;
  city: string;
  venue_name: string;
  latitude: string;
  longitude: string;
  currency: string;
  shares_venue: boolean;
};

export type Meta = {
  parks: Park[];
  brands: Array<{ brand_key: string; name: string }>;
  channels: Array<{ channel_key: string; name: string; kind: string; paid: boolean }>;
  leadStages: Array<{
    stage_id: string;
    name: string;
    funnel_order: number | null;
    counts_in_funnel: boolean;
    is_dump: boolean;
    is_handoff_stage: boolean;
  }>;
  settings: Array<{ key: string; value: string; description: string | null }>;
};

export type Overview = {
  filters: { from: string; to: string; era: string };
  leads: {
    kpi: Record<string, number>;
    /** Воронка: сколько лидов КОГДА-ЛИБО доходило до ступени (история стадий). */
    funnel: Array<{ stage_id: string; stage_name: string; funnel_order: number; leads: number }>;
    /** Где лиды стоят сейчас. Это срез, а не воронка — не путать. */
    stageNow: Array<{
      stage_id: string;
      stage_name: string;
      funnel_order: number | null;
      counts_in_funnel: boolean;
      is_dump: boolean;
      leads: number;
    }>;
    byChannel: Array<{
      channel_key: string;
      channel_name: string;
      leads: number;
      /** Квалифицированные лиды: Interested / Park Tour или завершённый праздник. */
      qualified: number;
      with_deal: number;
      with_won_deal: number;
      /** Чья метка. `analytics_tag` — наша, и это должно быть видно. */
      basis_client_tag: number;
      basis_analytics_tag: number;
      basis_chat_link: number;
      basis_source_field: number;
    }>;
    byPark: Array<{
      park_key: string;
      park_name: string;
      leads: number;
      qualified: number;
      with_deal: number;
      with_won_deal: number;
    }>;
    daily: Array<{ d: string; leads: number; qualified: number }>;
    utmCoverage: Array<{ basis_key: string; field_set: string; leads: number }>;
    /** С какой даты есть история переходов; раньше воронка недостоверна. */
    historyFrom: string | null;
  };
  deals: {
    kpi: Record<string, number | string>;
    byStage: Array<{
      stage_id: string;
      stage_name: string;
      funnel_order: number | null;
      deals: number;
      amount_aed_minor: string;
      /** Сколько из них пришло из объединённой стадии, и из какой. */
      merged_deals: number;
      merged_from: string | null;
    }>;
    byPark: Array<{
      park_key: string;
      park_name: string;
      deals: number;
      won: number;
      revenue_aed_minor: string;
      deposit_aed_minor: string;
      revenue_native_minor: string;
      native_currency: string | null;
    }>;
    display: { currency: string; minorUnits: number; mixed: boolean };
  };
  pipelineConversion: Array<{
    park_name: string;
    reached_handoff: number;
    with_deal: number;
    won_deal: number;
    conv_to_deal_pct: string | null;
    revenue_aed_minor: string;
  }>;
  freshness: Array<{
    source: string;
    entity: string;
    status: string;
    finished_at: string | null;
    duration_ms: number | null;
    rows_upserted: number;
    error: string | null;
  }>;
  queryMs: number;
};

export type ParkRevenue = {
  parks: Array<{
    park_key: string;
    revenue_aed_minor: string;
    revenue_native_minor: string;
    currency: string;
    tickets_aed_minor: string;
    food_aed_minor: string;
    /** Чеки праздников целиком (миграция 090), а не строки банкетных категорий. */
    banquet_aed_minor: string;
    /** Сколько чеков праздников за ней стоит. */
    party_checks: number;
    /** Банкетные категории кассы — только для кольца «Состав выручки», где доли складываются в итог. */
    banquet_category_aed_minor: string;
    other_aed_minor: string;
    ticket_receipts: number;
    /** Проданные билетные единицы. Прежнее поле guests было суммой по строкам отчёта OLAP и завышало в 3,5 раза. */
    tickets_sold: number;
    online_revenue_aed_minor: string;
    online_bookings: number;
  }>;
  coverage: Array<{ park_key: string; first_day: string | null; last_day: string | null }>;
};

export type BrandRow = {
  brand_key: string;
  brand_name: string;
  parks: number;
  leads: number;
  qualified: number;
  with_deal: number;
  with_won_deal: number;
  deals: number;
  deals_won: number;
  banquet_crm_aed_minor: string;
  /** Средний чек на завершённый праздник. `null`, когда сделок с суммой нет. */
  avg_party_aed_minor: string | null;
  deposits_aed_minor: string;
  pos_aed_minor: string;
  pos_tickets_aed_minor: string;
  pos_food_aed_minor: string;
  pos_banquet_aed_minor: string;
  ticket_receipts: number;
  /** Проданные билетные единицы. Прежнее поле guests было суммой по строкам отчёта OLAP и завышало в 3,5 раза. */
    tickets_sold: number;
  online_aed_minor: string;
  online_bookings: number;
};

export type BrandReport = {
  brands: BrandRow[];
  parks: Array<{
    park_key: string;
    park_name: string;
    brand_key: string;
    currency: string;
    city: string;
    country: string;
    leads: number;
    qualified: number;
    with_won_deal: number;
    deals_won: number;
    banquet_crm_aed_minor: string;
    avg_party_aed_minor: string | null;
    deposits_aed_minor: string;
    pos_aed_minor: string;
    pos_native_minor: string;
    ticket_receipts: number;
    /** Проданные билетные единицы. Прежнее поле guests было суммой по строкам отчёта OLAP и завышало в 3,5 раза. */
    tickets_sold: number;
    online_aed_minor: string;
    online_bookings: number;
  }>;
  monthly: Array<{
    month: string;
    brand_key: string;
    brand_name: string;
    /** Все обращения месяца. */
    leads: number;
    /** Из них квалифицированные: Interested / Park Tour или завершённый праздник. */
    qualified: number;
    won: number;
    pos_aed_minor: string;
  }>;
  coverage: Array<{
    park_key: string;
    first_day: string | null;
    last_day: string | null;
    /** Пришли ли чеки за ВЫБРАННЫЙ период, а не когда-либо вообще. */
    in_period: boolean;
  }>;
  /**
   * Лиды, не попавшие ни в одну карточку и ни в одну строку таблицы: у них нет
   * бренда или нет парка. Без этого числа три экрана показывают три разных
   * итога для одного и того же.
   */
  unattributed: {
    leads_total: number;
    leads_no_brand: number;
    leads_no_park: number;
    /** Покупки билетов с сайта: исключены из всех лидовых чисел экрана. */
    leads_ticket_purchase: number;
  };
  queryMs: number;
};

export type PipelineReport = {
  kpi: {
    /** Когорта: лиды, вошедшие в работу в периоде. */
    reached_handoff: number;
    reached_park_tour: number;
    with_deal: number;
    deal_from_interested: number;
    deal_from_park_tour: number;
    deal_direct: number;
    won_direct: number;
    won_deal: number;
    lost_deal: number;
    revenue_aed_minor: string;
    pipeline_aed_minor: string;
    /** Итог периода: считается по датам событий, а не по когорте. */
    closed_deposits: number;
    closed_deposits_aed_minor: string;
    secured_deposits: number;
    secured_value_aed_minor: string;
    completed_events: number;
    /** Сами депозиты по тем же сделкам: подпись раньше выдавала стоимость банкета за депозит. */
    secured_deposit_aed_minor: string;
    completed_revenue_aed_minor: string;
    /** Сколько исключений сверки изменили праздники периода. */
    exceptions_affecting: number;
  };
  byChannel: Array<{
    channel_key: string;
    channel_name: string;
    reached_handoff: number;
    with_deal: number;
    won_deal: number;
    conv_to_deal_pct: string | null;
    conv_to_won_pct: string | null;
    revenue_aed_minor: string;
  }>;
  byManager: Array<{
    manager_name: string;
    reached_handoff: number;
    with_deal: number;
    won_deal: number;
    conv_to_deal_pct: string | null;
    revenue_aed_minor: string;
  }>;
  byPark: Overview["pipelineConversion"];
  /** Время первого ответа на обращение и сколько обращений ждут до сих пор. */
  response: {
    n: number;
    unanswered: number;
    median_minutes: string | null;
    median_human_minutes: string | null;
    by_bot: number;
    by_human: number;
  };
  speed: {
    n: number;
    median_hours_to_handoff: string | null;
    p90_hours_to_handoff: string | null;
    median_hours_to_deal: string | null;
  };
  /**
   * Блок «Deposits to fix»: всё, что не сошлось между сделкой и оплатой в
   * системе бронирования (миграция 097). `kind` говорит, какой список
   * открывается под числом: сделки или брони. `counted` — входит ли строка в
   * Closed deposits.
   */
  depositReview: DepositReviewRow[];
  /**
   * Причины провала лидов (миграция 157): лиды, закрытые в провальную стадию в
   * периоде. Стадии и причины — из справочников портала, поэтому список меняется
   * вместе с ним. `reason_key` — номер значения строкой, `none` — не указана;
   * `name: null` — номер, который справочник ещё не подтянул.
   */
  lossReasons: {
    stages: Array<{ stage_id: string; name: string; in_portal: boolean }>;
    reasons: Array<{ reason_key: string; name: string | null; in_portal: boolean }>;
    cells: Array<{ stage_id: string; reason_key: string; leads: number }>;
  };
  queryMs: number;
};

export type DepositReviewRow = {
  key:
    | "deposit_unconfirmed"
    | "deposit_paid_wrong_stage"
    | "duplicate_open"
    | "park_from_booking"
    | "on_lead"
    | "without_deal";
  kind: "deals" | "payments";
  counted: boolean;
  count: number;
  aed_minor: string;
};

/** Оплата в системе бронирования, которая не стала депозитом сделки. */
export type DepositPaymentRow = {
  booking_id: string;
  ref: string | null;
  status: string;
  payment_method: string | null;
  manager_name: string | null;
  paid_local: string | null;
  party_local: string | null;
  park_key: string | null;
  park_name: string | null;
  currency: string;
  deposit_minor: string;
  deposit_aed_minor: string;
  total_aed_minor: string;
  crm_entity_type: string | null;
  crm_deal_id: string | null;
  crm_lead_id: string | null;
  deal_deleted: boolean;
  /** Сделки этого лида в воронке Banquet — куда перепривязать бронь. */
  lead_deals: string | null;
  lead_url: string | null;
  deal_url: string | null;
};

export type DepositPaymentPage = {
  total: number;
  sum_aed_minor: string;
  rows: DepositPaymentRow[];
  limit: number;
  offset: number;
};

/**
 * Сверка двух сумм депозитов, стоящих на одном экране. Обе стороны
 * раскладываются без остатка: both + only = вся сумма своей стороны.
 */
export type DepositReconcile = {
  crm_both_aed_minor: string;
  booking_both_aed_minor: string;
  both_deals: number;
  crm_only_deals: number;
  crm_only_aed_minor: string;
  booking_only: number;
  booking_only_aed_minor: string;
};

export type OperationsReport = {
  reconcile: DepositReconcile;
  /** Деньги, пришедшие по сделкам с пустым полем депозита в CRM. */
  uncounted: { deals: number; aed_minor: string };
  deposits: Record<string, string | number>;
  paymentFunnel: {
    bookings: number;
    link_sent: number;
    paid: number;
    expired: number;
    cancelled: number;
    on_hold: number;
    paid_aed_minor: string;
  };
  timing: { n: number; median_hours: string | null; p90_hours: string | null; within_1h: number; within_24h: number };
  /**
   * Ближайшие праздники числом, а не строками (с 19.10.2026). Окно `from_day..
   * to_day` считает сервер — дубайскими датами, — и плитка отдаёт его списку
   * под числом как есть: дата браузера бывает UTC и уехала бы на день.
   */
  upcoming: {
    from_day: string;
    to_day: string;
    events: number;
    /** Сделки без единой оплаченной брони — срез `hasPaidBooking: false`. */
    without_paid_booking: number;
  };
  attention: Array<{ issue: string; label: string; deals: number; amount_aed_minor: string }>;
  byPark: Array<{
    park_name: string;
    park_key: string | null;
    deals: number;
    won: number;
    open: number;
    deposit_aed_minor: string;
    revenue_aed_minor: string;
    pipeline_aed_minor: string;
  }>;
  upcomingDays: number;
  queryMs: number;
};

/**
 * Вилка ROMI одной темы. Низ — только то, что донесло метку рекламы, верх — всё
 * за период; охват — доля прослеженного. Счётчики — праздники у банкетов и
 * оплаченные брони у билетов.
 */
export type RomiRange = {
  spend_aed_minor: string;
  attributed_revenue_aed_minor: string;
  attributed_count: number;
  all_revenue_aed_minor: string;
  all_count: number;
  romi_attributed_pct: string | null;
  romi_upper_pct: string | null;
  attribution_coverage_pct: string | null;
};

export type MarketingReport = {
  /** Фильтры, с которыми посчитан ответ, — сервер возвращает их вместе с данными. */
  filters: { from: string; to: string; era: string };
  kpi: {
    spend_aed_minor: string;
    spend_park_aed_minor: string;
    spend_brand_aed_minor: string;
    spend_unknown_aed_minor: string;
    conversations: string;
    /**
     * Конверсии кабинета РАЗДЕЛЬНО по виду цели.
     *
     * Единого числа «платформенных лидов» больше нет, и это не косметика: оно
     * складывало заявки поисковых кампаний с подписками Demand Gen и давало
     * 30 103 при 4 124 лидоподобных.
     */
    lead_conversions: string;
    /**
     * Части `lead_conversions` по виду действия: обращения (звонок, форма,
     * чат) и нажатия «Маршрут» в Google Maps. Остаток — прочее (переходы с
     * сайта, покупки). Замер за октябрь 2026: из 865 маршрутов 782.
     */
    lead_inquiry_conversions: string;
    lead_directions_conversions: string;
    engagement_conversions: string;
    awareness_conversions: string;
    other_conversions: string;
    impressions: string;
    clicks: string;
  };
  byPark: Array<{
    park_key: string;
    park_name: string;
    brand_key: string;
    spend_aed_minor: string;
    conversations: string;
    clicks: string;
    lead_conversions: string;
    leads: number;
    qualified: number;
    won: number;
    revenue_aed_minor: string;
    has_pos_data: boolean;
    has_ad_data: boolean;
    /** Кабинет привязан к парку вообще — вопрос, не зависящий от периода. */
    has_ad_account: boolean;
  }>;
  byCampaign: Array<{
    campaign_external_id: string;
    campaign_name: string;
    objective: string | null;
    account_name: string;
    park_name: string | null;
    attribution_kind: string;
    spend_aed_minor: string;
    impressions: string;
    clicks: string;
    conversations: string;
    platform_conversions: string;
    /** Что кабинет считает конверсией у этой цели. */
    conversion_kind: "lead" | "engagement" | "awareness" | "other";
  }>;
  monthly: Array<{ month: string; spend_aed_minor: string; conversations: string; leads: number }>;
  unattributed: Array<{
    name: string;
    objective: string | null;
    account_name: string;
    first_day: string;
    last_day: string;
    spend_aed_minor: string;
    messaging_conversations: string;
    leads: string;
  }>;
  coverage: Array<{
    platform: string;
    external_id: string;
    name: string;
    currency: string;
    brand_key: string | null;
    default_park_key: string | null;
    dormant: boolean;
    enabled: boolean;
    first_day: string | null;
    last_day: string | null;
  }>;
  leadTotals: { leads: number; park_unknown: number };
  /**
   * Воронка по кампаниям — единственный разрез с атрибуцией до кампании.
   * Периодом НЕ фильтруется: таких лидов 86 за три года.
   */
  campaignFunnel: Array<{
    campaign_name: string;
    objective: string | null;
    account_name: string;
    park_key: string | null;
    attribution_kind: string;
    spend_aed_minor: string;
    conversations: string;
    leads_with_utm: number;
    leads_with_inferred_tag: number;
    leads_total: number;
    with_deal: number;
    won_deal: number;
    revenue_aed_minor: string;
    first_day: string | null;
    last_day: string | null;
  }>;
  utmLevels: Array<{
    platform: string;
    utm_field: string;
    utm_level: string;
    distinct_objects: number;
    campaigns: number;
    leads: number;
    first_lead: string;
    last_lead: string;
  }>;
  /**
   * Раздельно по платформам. Расход и конверсии — из кабинета, лиды — из CRM по
   * единому определению источника. Рядом, но не в сумме: это разные счётчики.
   */
  byPlatform: Array<{
    platform: string;
    spend_aed_minor: string;
    impressions: string;
    clicks: string;
    conversations: string;
    lead_conversions: string;
    engagement_conversions: string;
    awareness_conversions: string;
    crm_leads: number;
    crm_leads_client_tag: number;
    crm_leads_analytics_tag: number;
    crm_leads_chat_link: number;
    crm_qualified: number;
    crm_won: number;
    crm_revenue_aed_minor: string;
  }>;
  /** Источники со стороны CRM — то же определение, что в Channels на Overview. */
  bySource: Array<{
    source_group: string;
    source_name: string;
    channel_paid: boolean | null;
    leads: number;
    qualified: number;
    with_deal: number;
    won: number;
    basis_analytics_tag: number;
    tag_unmapped: number;
    revenue_aed_minor: string;
  }>;
  /**
   * ROMI вилкой, а не числом. Низ считает только привязанные к рекламе сделки и
   * потому занижает ровно настолько, насколько неполна атрибуция; верх берёт всю
   * выручку периода и потому завышает. Охват показывает, где внутри вилки правда.
   */
  romi: {
    /** Весь расход периода — для сверки: праздники + билеты + прочее. */
    spend_aed_minor: string;
    /** Кампании без темы в названии — не входят ни в один ROMI. */
    other_spend_aed_minor: string;
    /** По праздникам: расход кампаний «birthday», числитель — завершённые праздники. */
    banquet: RomiRange;
    /** По онлайн-билетам: расход кампаний «ticket», числитель — оплаченные брони сайта. */
    tickets: RomiRange;
  };
  /** Онлайн-билеты по источникам (миграция 093, `api/ticketSources.ts`). */
  tickets: TicketSourceReport;
  queryMs: number;
};

/**
 * Источник брони сайта билетов — ключ `core.v_ticket_booking_source.source_key`.
 * Подписи — в словаре экрана: ключ один на два языка.
 */
export type TicketSourceKey =
  | "google_ads"
  | "meta_ads"
  | "meta_organic"
  | "meta_unmarked"
  | "google_organic"
  | "own_site"
  | "referral"
  | "other_tag"
  | "service"
  | "no_source"
  | "not_recorded";

/**
 * Онлайн-билеты по источникам.
 *
 * Попытки — по дню создания брони, оплаты и выручка — по дню оплаты, как у
 * витрины онлайн-билетов: выручка здесь в сумме равна выручке онлайн-билетов
 * на Overview. Конверсия — доля попыток периода, закончившихся оплатой.
 */
export type TicketSourceReport = {
  kpi: {
    attempts: number;
    attempts_paid: number;
    paid: number;
    tickets: number;
    revenue_aed_minor: string;
    paid_channel_known: number;
    paid_own_site: number;
    paid_unknown: number;
    conversion_pct: string | null;
    avg_check_aed_minor: string | null;
    channel_known_pct: string | null;
    own_site_pct: string | null;
    unknown_pct: string | null;
  };
  bySource: Array<{
    source_key: TicketSourceKey;
    attempts: number;
    attempts_paid: number;
    paid: number;
    tickets: number;
    revenue_aed_minor: string;
  }>;
  /** Три самых частых сайта, площадки или кампании внутри источника; `kinds` — сколько их всего. */
  details: Array<{ source_key: TicketSourceKey; detail: string; n: number; kinds: number }>;
  byCampaign: Array<{
    platform: "google" | "meta";
    campaign_id: string;
    campaign_name: string | null;
    account_name: string | null;
    objective: string | null;
    /** Кампания есть в подключённых кабинетах. Нет — расход неизвестен. */
    in_catalog: boolean;
    spend_aed_minor: string;
    clicks: string;
    /** Покупки по подсчёту самой площадки (пиксель Meta). */
    platform_purchases: string;
    attempts: number;
    attempts_paid: number;
    paid: number;
    tickets: number;
    revenue_aed_minor: string;
  }>;
  /** Первая бронь с записанным источником — с какого момента сайт его пишет. */
  since: string | null;
};

/** Срез списка броней под цифрой блока билетов. */
export type TicketSlice = {
  basis: "attempts" | "paid";
  source?: TicketSourceKey;
  campaign?: { platform: string; id: string };
};

export type TicketBookingRow = {
  booking_id: string;
  reference: string | null;
  created_at: string;
  paid_at: string | null;
  /** То же время по Дубаю строкой `YYYY-MM-DD HH:MM`. */
  created_local: string;
  paid_local: string | null;
  status: string;
  park_key: string | null;
  park_name: string | null;
  ticket_name: string | null;
  ticket_kind: string | null;
  is_promo_ticket: boolean;
  quantity: number;
  currency: string;
  total_amount_minor: string;
  total_aed_minor: string;
  coupon_code: string | null;
  source_key: TicketSourceKey;
  basis: "click_id" | "utm" | "referrer" | "none" | "not_recorded";
  detail: string | null;
  platform: "google" | "meta" | null;
  paid_kind: string;
  campaign_id: string | null;
  campaign_name: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  gclid: string | null;
  fbclid: string | null;
  referrer: string | null;
  landing_page: string | null;
  crm_lead_id: string | null;
  lead_url: string | null;
};

export type TicketBookingPage = {
  total: number;
  paid: number;
  sum_aed_minor: string;
  rows: TicketBookingRow[];
  limit: number;
  offset: number;
};

export type JourneyReport = {
  kpi: {
    cycles: number;
    people: number;
    repeat_cycles: number;
    won_cycles: number;
    with_campaign: number;
    revenue_aed_minor: string;
    avg_touches: string | null;
  };
  /** `step_key` — ключ для словаря интерфейса, `step_name` — английская подпись из SQL. */
  funnel: Array<{ step_order: number; step_key: string; step_name: string; cycles: number }>;
  byFirstChannel: Array<{
    channel_key: string;
    channel_name: string;
    cycles: number;
    won: number;
    revenue_aed_minor: string;
    win_pct: string | null;
  }>;
  conversionPath: Array<{
    first_channel_key: string;
    last_channel_key: string;
    first_channel: string;
    last_channel: string;
    cycles: number;
    won: number;
  }>;
  topCampaigns: Array<{
    campaign_name: string;
    source: "utm" | "inferred";
    cycles: number;
    won: number;
    revenue_aed_minor: string;
  }>;
  coverage: Array<{ confidence: string; leads: number }>;
  queryMs: number;
};

/**
 * Справочник пути клиента: поиск человека и его путь.
 *
 * Отдельно от `JourneyReport`, потому что это другой вопрос. Тот отвечает «как
 * ходят люди вообще», этот — «а вот этот человек как прошёл». И запросы разные:
 * поиск дёргается на ввод, путь — раз на выбранного человека.
 */
export type JourneySearchHit = {
  person_key: string;
  display_name: string | null;
  phone_e164: string | null;
  email: string | null;
  leads: number;
  cycles: number;
  first_seen: string | null;
  last_seen: string | null;
  parks: string[];
  won_cycles: number;
  revenue_aed_minor: string;
};

export type JourneySearchResult = {
  q: string;
  hits: JourneySearchHit[];
  truncated: boolean;
  hint: string | null;
  queryMs: number;
};

export type JourneyTouch = {
  touch_at: string;
  touch_kind: "prompt" | "inquiry" | "reached_manager" | "deal_created" | "paid" | "won";
  touch_name: string;
  lead_id: string | null;
  deal_id: string | null;
  channel_key: string | null;
  channel_name: string | null;
  park_key: string | null;
  campaign_name: string | null;
  campaign_source: "utm" | "inferred" | null;
  attribution_confidence: string | null;
  amount_aed_minor: string;
};

export type JourneyPerson = {
  person: {
    person_key: string;
    display_name: string | null;
    phone_e164: string | null;
    email: string | null;
    identified_by_lead_only: boolean;
  } | null;
  cycles: Array<{
    cycle_no: string;
    started_at: string;
    last_touch_at: string;
    touches: string;
    leads: string;
    first_channel: string | null;
    last_channel: string | null;
    first_campaign: string | null;
    first_campaign_source: "utm" | "inferred" | null;
    first_park: string | null;
    reached_manager: boolean;
    became_deal: boolean;
    paid: boolean;
    won: boolean;
    revenue_aed_minor: string;
    is_repeat_cycle: boolean;
  }>;
  touches: JourneyTouch[];
  leads: Array<{
    lead_id: string;
    title: string | null;
    date_create: string;
    stage_name: string | null;
    status_semantic: string | null;
    park_key: string | null;
    channel_key: string | null;
    channel_name: string | null;
    source_id: string | null;
    source_note: string | null;
    assigned_name: string | null;
    inquiry_type: string | null;
    bot_flag: boolean;
    ai_summary: string | null;
    utm_source: string | null;
    utm_campaign: string | null;
    utm_field_set: string | null;
  }>;
  deals: Array<{
    deal_id: string;
    lead_id: string | null;
    title: string | null;
    date_create: string;
    closedate: string | null;
    date_of_event: string | null;
    stage_name: string | null;
    stage_semantic: string | null;
    park_key: string | null;
    opportunity_aed_minor: string | null;
    deposit_aed_minor: string | null;
    actual_minor: string | null;
    actual_currency: string | null;
    assigned_name: string | null;
    paid_at: string | null;
    guests: number | null;
    /** Сколько броней у сделки. Раньше каждая бронь давала лишнюю строку сделки. */
    bookings: number;
  }>;
  attribution: Array<{
    lead_id: string;
    confidence: string;
    link_kind: string | null;
    link_url: string | null;
    matched_key: string | null;
    platform: string | null;
    ad_external_id: string | null;
    campaign_name: string | null;
    candidates: number;
    delivery_confirmed: boolean;
    message_at: string | null;
    post_permalink: string | null;
    decided_at: string;
    written_at: string | null;
    written_utm_source: string | null;
    skipped_reason: string | null;
  }>;
  queryMs: number;
};

export type SalesWeekly = {
  weeks: Array<{
    week_start: string;
    week_end: string;
    pipeline_start: number;
    entered_pipeline: number;
    worked_total: number;
    incoming: number;
    qualified_bot: number;
    deals_created: number;
    deals_won: number;
    won_via_pipeline: number;
    won_bypassed: number;
    leads_lost: number;
    deposits_paid: number;
    deposit_aed_minor: string;
    revenue_won_aed_minor: string;
    pipeline_end: number;
    /** Все закрытые лиды недели: won_via_pipeline + won_bypassed. */
    leads_won: number;
    /** Выигранные сделки без лида вовсе — ни в одну конверсию не входят. */
    won_no_lead: number;
    /** Полная конверсия — все закрытые лиды к всему объёму работы. */
    conversion_full_pct: number | null;
    conversion_pct: number | null;
    conversion_naive_pct: number | null;
    is_current: boolean;
    pipeline_known: boolean;
    /** Когда пересчитывалась витрина недельного отчёта. */
    rebuilt_at: string | null;
  }>;
  byPark: Array<{
    week_start: string;
    park_name: string;
    park_key: string;
    brand_name: string;
    pipeline_start: number;
    entered_pipeline: number;
    worked_total: number;
    incoming: number;
    qualified_bot: number;
    deals_won: number;
    won_via_pipeline: number;
    won_bypassed: number;
    leads_lost: number;
    deposits_paid: number;
    deposit_aed_minor: string;
    revenue_won_aed_minor: string;
    pipeline_end: number;
    leads_won: number;
    won_no_lead: number;
    conversion_full_pct: number | null;
    conversion_pct: number | null;
    conversion_naive_pct: number | null;
    avg_check_aed: number | null;
  }>;
  ageing: Array<{
    park_name: string;
    park_key: string;
    brand_key: string;
    /** `age_key` — ключ для словаря, `age_bucket` — английская подпись из представления. */
    age_key: string;
    age_bucket: string;
    age_order: number;
    leads: number;
    avg_days: number;
    oldest_days: number;
  }>;
  lossReasons: Array<{ loss_stage: string; was_in_pipeline: boolean; leads: number }>;
  avgCheck: Array<{
    month: string;
    deals_won: number;
    revenue_aed_minor: string;
    avg_check_aed: number;
    median_check_aed: number;
  }>;
  managers: Array<{
    manager_name: string;
    pipeline_now: number;
    entered_period: number;
    won: number;
    /** Различные закрытые лиды — числитель конверсии менеджера. */
    leads_won: number;
    won_via_pipeline: number;
    /** Выигранные сделки без лида: в конверсию не входят. */
    won_no_lead: number;
    revenue_aed_minor: string;
    median_days_to_close: string | null;
    conversion_pct: number | null;
    avg_check_aed: number | null;
  }>;
  coverage: {
    history_from: string;
    leads_with_inquiry_type: number;
    leads_total: number;
    deals_with_prepay_date: number;
    deals_managed: number;
    payments_from: string;
    payments_total: number;
  };
  queryMs: number;
};

export type Me = {
  sub: string;
  name: string;
  isAdmin: boolean;
  screens: string[];
  via: string;
  exp: number;
};

export type AppUser = {
  id: number;
  username: string | null;
  name: string;
  bitrix_user_id: string | null;
  is_admin: boolean;
  screens: string[];
  active: boolean;
  note: string | null;
  created_at: string;
  created_by: string | null;
  last_login_at: string | null;
};

export type AppSetting = {
  key: string;
  value: string;
  description: string | null;
  operator_editable: boolean;
  value_kind: "bool" | "int" | "date" | "text" | "enum" | "idlist";
  /** Заполнено только у `enum`: интерфейс рисует по нему список, сервер проверяет. */
  allowed_values: string[] | null;
};

/**
 * Решение сторожа открытых линий по одному диалогу.
 *
 * Здесь есть и «ничего не сделали»: именно эти строки объясняют, почему
 * конкретному клиенту не перевели, — без них включённый сторож выглядел бы
 * неработающим.
 */
export type WatchdogAction = {
  session_id: number;
  decided_at: string;
  verdict:
    | "answered"
    | "transferred"
    | "would_transfer"
    | "service_owner"
    | "no_target"
    | "out_of_scope"
    | "failed";
  line_name: string | null;
  owner_type: number;
  owner_id: number;
  waited_minutes: number | null;
  target_entity: "deal" | "lead" | null;
  target_id: number | null;
  target_user_id: number | null;
  target_user_name: string | null;
  error: string | null;
  link: string;
  /**
   * Запасной путь по парку (миграция 103). `assigned` — диалог отдан менеджеру
   * парка из таблицы, остальное — почему не сработал (у предупреждения).
   */
  park_fallback: "assigned" | "no_manager" | "no_park" | "manager_inactive" | "lead_closed" | null;
  fallback_park_key: string | null;
  fallback_park_name: string | null;
  lead_reassigned: boolean | null;
};

/** Менеджер парка для сторожа: кому отдать брошенный лид на служебной учётной записи. */
export type WatchdogParkManagerRow = {
  /** `null` — строка «парк не определён». */
  park_key: string | null;
  park_name: string | null;
  bitrix_user_id: number | null;
  manager_name: string | null;
  /** `false` — назначенный уволен, бот или служебная учётная запись. */
  usable: boolean | null;
  updated_by: string | null;
  updated_at: string | null;
};

export type WatchdogParkManagers = {
  rows: WatchdogParkManagerRow[];
  candidates: Array<{ bitrix_user_id: number; name: string }>;
};

/** Когда сторож последний раз смотрел линии — и смотрит ли вообще. */
export type WatchdogRun = {
  scheduled: boolean;
  every_minutes: number | null;
  last_status: string | null;
  last_finished_at: string | null;
  last_error: string | null;
  minutes_ago: number | null;
  is_stale: boolean;
};

export type WatchdogSummary = {
  day: Record<string, number>;
  transfers_total: number;
  would_total: number;
  warnings_total: number;
  last_decision_at: string | null;
};

export type SettingChange = {
  key: string;
  old_value: string | null;
  new_value: string;
  changed_by: string;
  changed_at: string;
};

/** Чем заблокирован кандидат: части, из которых фразу собирает интерфейс. */
export type CohortBlockedBy = {
  lead_id: string;
  /** Имя стадии из Битрикса — не переводится ни на каком языке. */
  stage: string;
  created: string;
};

export type CohortCandidate = {
  source_deal_id: string;
  phone_e164: string | null;
  title: string | null;
  park_key: string | null;
  park_name: string | null;
  event_date: string | null;
  amount_aed_minor: string | null;
  previous_manager_id: string | null;
  previous_manager_name: string | null;
  /** Ключ причины: no_phone | already_created | active_lead | active_deal | no_park. */
  skip_reason: string | null;
  blocked_by: CohortBlockedBy | null;
};

export type CohortPreview = {
  candidates: CohortCandidate[];
  summary: { total: number; eligible: number; skipped: number };
  byPark: Array<{ park_key: string; park_name: string; eligible: number; skipped: number }>;
  queryMs: number;
};

export type CohortRun = {
  id: string;
  cohort: string;
  event_from: string;
  event_to: string;
  park_keys: string[] | null;
  requested: number;
  created: number;
  skipped: number;
  failed: number;
  started_by: string;
  /** Имя того, кто запустил; для автозапуска сервер пишет `auto`. */
  started_by_name: string | null;
  /** Месяц автозапуска (`YYYY-MM-DD`, первое число); у ручных прогонов — null. */
  auto_month: string | null;
  started_at: string;
  finished_at: string | null;
  error: string | null;
};

/** Автопрогон одного месяца — строка `meta.cohort_run` с `auto_month`. */
export type CohortAutoRun = {
  id: string;
  auto_month: string;
  event_from: string;
  event_to: string;
  requested: number;
  created: number;
  failed: number;
  skipped: number;
  started_at: string;
  finished_at: string | null;
};

/**
 * Автозапуск 1-го числа: рубильник и что из него следует.
 *
 * `verdict` — где мы относительно запуска текущего месяца: off | done | resume |
 * not_yet | enabled_late | due (см. `server/src/api/cohortAuto.ts`).
 */
export type CohortAutoStatus = {
  enabled: boolean;
  hourDubai: number;
  changedAt: string | null;
  changedBy: string | null;
  verdict: "off" | "done" | "resume" | "not_yet" | "enabled_late" | "due";
  /** Ближайший запуск; у выключенного — каким он был бы, если включить сейчас. */
  next: {
    month: string;
    dueAt: string;
    eventFrom: string;
    eventTo: string;
    total: number;
    eligible: number;
  };
  cap: number;
  current: CohortAutoRun | null;
  last: CohortAutoRun | null;
  check: {
    everyMinutes: number | null;
    scheduleEnabled: boolean;
    schedulerEnabled: boolean;
    lastAt: string | null;
    lastStatus: string | null;
    lastError: string | null;
  };
};

export type AppScreen = {
  key: string;
  title: string;
  description: string | null;
  sensitive: boolean;
  sort: number;
};

export type RunningJob = {
  source: string;
  entity: string;
  started_at: string;
  seconds: number;
};

/**
 * Состояние пула соединений к базе.
 *
 * Занятые соединения, которые не возвращаются, — это то, от чего 15.09.2026
 * падал прод. С арендой вместо advisory-лока прогоны ETL соединение больше не
 * держат, и «занято» здесь означает только живую работу.
 */
export type PoolStats = {
  app: { total: number; idle: number; waiting: number; max: number };
};

export type SourceHealth = {
  source: string;
  entity: string;
  enabled: boolean;
  last_status: string | null;
  last_finished_at: string | null;
  last_rows: number | null;
  minutes_ago: string | null;
  is_stale: boolean;
  last_error: string | null;
};

export type ExportDataset = { key: string; title: string; note: string; columns: number };

// ---------------------------------------------------------------- вызовы


/**
 * Строка списка лидов под цифрой на экране.
 *
 * `bitrix_url` собирает сервер, а не фронтенд: адрес портала лежит в настройках
 * сервера, и смена портала не должна требовать пересборки интерфейса.
 */
export type LeadRow = {
  lead_id: number;
  title: string | null;
  phone_e164: string | null;
  email: string | null;
  created: string;
  source_group: string;
  source_name: string;
  basis: "client_tag" | "analytics_tag" | "chat_link" | "source_field" | "site_ref";
  tag_source: string | null;
  tag_medium: string | null;
  tag_unmapped: boolean;
  park_name: string | null;
  stage_name: string | null;
  manager_name: string | null;
  inquiry_type: string | null;
  reached_handoff: boolean;
  has_deal: boolean;
  has_won_deal: boolean;
  deal_id: number | null;
  deal_stage_name: string | null;
  deal_stage_semantic: string | null;
  deal_amount_aed_minor: string | null;
  date_of_event: string | null;
  bitrix_url: string;
  deal_bitrix_url: string | null;
};

export type LeadListPage = {
  total: number;
  rows: LeadRow[];
  limit: number;
  offset: number;
  portal: string;
};

/**
 * Строка списка СДЕЛОК под цифрой на экране.
 *
 * Отдельно от `LeadRow`, а не полем в нём: у сделки бывает НЕТ лида, и таких
 * сделок под плитками «Требует внимания» 24 из 105 — в строке «выиграна, но
 * депозит не записан» 11 из 13. Список лидов под этой плиткой показал бы
 * 2 строки под числом 13.
 *
 * Деньги — целые в минорных единицах и приезжают СТРОКАМИ: bigint из Postgres
 * сериализуется строкой. Печатать их можно только через `money()`.
 */
export type DealRow = {
  deal_id: number;
  title: string | null;
  created: string;
  /** Настоящее имя стадии. Отбор идёт по отчётному — см. `DealSlice.stageId`. */
  stage_name: string | null;
  report_stage_id: string | null;
  report_stage_name: string | null;
  stage_semantic: string | null;
  outcome: "won" | "lost" | "open";
  park_name: string | null;
  brand_key: string | null;
  manager_name: string | null;
  /** Календарная дата праздника, `YYYY-MM-DD`. Без времени и без пересчёта. */
  date_of_event: string | null;
  closedate: string | null;
  currency: string | null;
  opportunity_minor: string | null;
  opportunity_aed_minor: string | null;
  deposit_minor: string | null;
  /**
   * Депозит сделки: поле CRM, а если оно пустое — подтверждённая оплата брони
   * (миграция 100). `deposit_from_booking` — взят из брони.
   */
  deposit_aed_minor: string | null;
  deposit_from_booking: boolean | null;
  /** Сколько добрать с клиента. NULL, когда у сделки нет суммы. */
  balance_aed_minor: string | null;
  lead_id: number | null;
  lead_title: string | null;
  lead_phone_e164: string | null;
  booking_ref: string | null;
  booking_paid_at: string | null;
  /** Момент депозита по правилу миграции 097 (Дубай, `YYYY-MM-DD HH:MM`); NULL — не подтверждён. */
  deposit_at: string | null;
  /** `payment` — оплата в системе бронирования; `stage` — вход в Booked до 01.09.2026. */
  deposit_basis: "payment" | "stage" | null;
  /** Первый вход в Booked или дальше по истории стадий. */
  booked_at: string | null;
  /** Основная сделка, если эта — дубль из хранилища исключений. */
  duplicate_of: string | null;
  /** ВСЕ расхождения этой сделки, а не только то, по которому её отобрали. */
  issue_flags: string[];
  /** Праздник с учётом исключений: день, проведён ли, сумма. Отличаются от CRM только у сделок с исключением. */
  party_date: string | null;
  party_completed: boolean;
  party_amount_minor: string | null;
  party_amount_aed_minor: string | null;
  /** Действующие исключения сделки — ключи `core.dim_exception_kind`. */
  exception_kinds: string[];
  bitrix_url: string;
  lead_bitrix_url: string | null;
};

/** Разбивка сделок среза по месяцам праздника. `month === null` — дата не заполнена. */
export type DealMonthsPage = {
  rows: Array<{
    month: string | null;
    deals: number;
    deposit_aed_minor: string;
    amount_aed_minor: string;
  }>;
  total: number;
};

/**
 * Чек праздника под плиткой «Выручка с банкетов (Syrve)».
 *
 * Сделка — по номеру брони из внешнего номера заказа, по итогу брони (совпал с
 * чеком до филса: резерв без предзаказа номер в чек не несёт) или по ручной
 * привязке сверки. Пустая сделка значит «не связан с бронью», а не «сделки
 * нет»: праздник без сделки в Битриксе помечен отдельно (`exception_kinds`).
 */
export type PartyCheckRow = {
  order_id: string;
  order_num: string | null;
  business_day: string;
  park_name: string | null;
  is_banquet: boolean;
  guest_name: string | null;
  booking_ref: string | null;
  currency: string;
  revenue_minor: string;
  revenue_aed_minor: string;
  deal_id: string | null;
  deal_link: "booking" | "booking_total" | "manual" | null;
  deal_title: string | null;
  deal_stage_name: string | null;
  deal_stage_semantic: string | null;
  deal_event_date: string | null;
  deal_url: string | null;
  exception_kinds: string[];
};

export type PartyCheckPage = {
  total: number;
  sum_aed_minor: string;
  rows: PartyCheckRow[];
  limit: number;
  offset: number;
};

/** Состояние исключения: см. `core.v_metric_exception`. */
export type ExceptionStatus = "active" | "standby" | "resolved" | "changed" | "missing" | "retired";

export type ExceptionRow = {
  exception_id: string;
  entity: "deal" | "pos_order";
  kind: string;
  effect: "exclude" | "redate" | "include" | "link" | "note";
  status: ExceptionStatus;
  label_ru: string;
  label_en: string;
  deal_id: string | null;
  deal_title: string | null;
  deal_stage_name: string | null;
  deal_event_date: string | null;
  order_id: string | null;
  order_num: string | null;
  order_day: string | null;
  order_revenue_minor: string | null;
  order_currency: string | null;
  park_name: string | null;
  event_date: string | null;
  seen_date: string | null;
  corrected_date: string | null;
  related_deal_id: string | null;
  amount_minor: string | null;
  currency: string | null;
  note_ru: string;
  note_en: string;
  source: "audit" | "detector" | "manual";
  source_ref: string | null;
  created: string;
  retired: string | null;
  retired_reason: string | null;
  deal_url: string | null;
  related_deal_url: string | null;
};

export type ExceptionPage = {
  total: number;
  rows: ExceptionRow[];
  kinds: Array<{ kind: string; effect: string; label_ru: string; label_en: string; rule_ru: string; rule_en: string }>;
};

export type DealListPage = {
  total: number;
  rows: DealRow[];
  limit: number;
  offset: number;
  portal: string;
};

/**
 * Срез списка сделок: те же признаки, по которым считаются плитки.
 *
 * `dateBasis` ОБЯЗАТЕЛЕН и без значения по умолчанию. Три плитки экрана
 * «Конверсия воронки» стоят на трёх разных датах — 403 сделки по моменту
 * закрытия депозита, 231 по дате праздника, 230 по дате закрытия, — и молчаливое
 * значение по умолчанию открыло бы под ними один и тот же список.
 */
export type DealSlice = {
  /* `paid` — дата, когда деньги реально пришли в систему бронирования.
     Плитка дайджеста «Платежи депозита подтверждены» считает именно её, а не
     поле сделки: поле заполняет человек. */
  /* `deposit` — момент депозита по правилу миграции 097 (оплата в системе
     бронирования); `deposit_review` — когда сделка вошла в оборот депозита:
     по ней отбирается блок «Deposits to fix». */
  dateBasis:
    | "create" | "create_local" | "closedate" | "event" | "party" | "booked" | "paid"
    | "deposit" | "deposit_review";
  issue?:
    | "won_without_amount"
    | "won_without_deposit"
    | "deposit_on_lost"
    | "deposit_over_amount"
    | "open_past_event"
    | "no_event_date"
    | "deposit_uncounted"
    | "deposit_unconfirmed"
    | "deposit_paid_wrong_stage"
    | "duplicate_open"
    | "park_from_booking";
  /** `not_lost` — выражение плитки Secured deposits: выигранные и в работе. */
  /** `held` — праздник проведён с учётом исключений: то, что считают плитки завершённых праздников. */
  outcome?: "won" | "lost" | "open" | "not_lost" | "held";
  /** Стадия ОТЧЁТНАЯ: слитые стадии-близнецы отбираются одной. */
  stageId?: string;
  parkKey?: string;
  parkUnknown?: boolean;
  brandKey?: string;
  managerId?: number;
  hasDeposit?: boolean;
  hasLead?: boolean;
  hasPaidBooking?: boolean;
  /** Депозит подтверждён (миграция 097): условие плитки Secured deposits. */
  depositConfirmed?: boolean;
  /** Окно праздника ОТДЕЛЬНО от периода: это разные вопросы. */
  eventFrom?: string;
  eventTo?: string;
  sourceGroup?: string;
  isPaidSource?: boolean;
  campaignExternalId?: string;
  search?: string;
};

/** Срез списка: те же признаки, по которым считаются плитки. */
export type LeadSlice = {
  sourceGroup?: string;
  parkKey?: string;
  stageId?: string;
  basis?: string;
  reached?: boolean;
  hasDeal?: boolean;
  won?: boolean;
  unmappedTag?: boolean;
  search?: string;
  brandKey?: string;
  /** Лиды, у которых парк не определён: на экранах это сентинел «(not identified)». */
  parkUnknown?: boolean;
  /**
   * Ступень воронки по ФАКТУ прохождения, а не текущая стадия. Это два разных
   * числа, и путать их нельзя: на стадии FOLLOW UP замерено 1 579 лидов,
   * когда-либо туда дошедших, против 813 стоящих там сейчас. Воронка на Обзоре
   * показывает первое, приписка «сейчас вне воронки» — второе.
   */
  reachedStageId?: string;
  /** Лид в мусорной стадии — подпись «закрыто как мусор» под плиткой Лиды. */
  isDump?: boolean;
  /**
   * Покупка билета с сайта (стадия Online Tickets). `false` убирает покупки
   * из списка, `true` оставляет только их. Экран «Бренды» считает лидами
   * только обращения, поэтому его списки идут с `false`.
   */
  ticketPurchase?: boolean;
  /**
   * Ведро возраста в воронке: 4 — «лежит больше 30 дней». Тем же признаком
   * считается карточка «Воронка стареет» на дайджесте. Совокупность не зависит
   * от периода, поэтому плитка, открывающая такой список, обязана задать своё
   * окно через `filters` у детализации.
   */
  ageOrder?: number;
  managerId?: number;
  /**
   * По какой дате резать период: создания лида или входа в воронку. Второе —
   * когорта экрана «Конверсия воронки», и она НЕ совпадает с первой: замерено
   * 1 292 против 1 162, потому что в неё попадают лиды, созданные раньше и
   * поднятые в работу внутри периода.
   */
  /* `created_local` — те же лиды, но сутки по времени парка. Нужен утреннему
     дайджесту: на потоке в две с лишним сотни обращений в день UTC-граница
     развела бы список с числом над ним на каждой полуночи. */
  /* `lost` — лид закрыт как проигранный в периоде (разбивка причин провала,
     миграция 157). */
  cohort?: "created" | "created_local" | "entered_handoff" | "lost";
  /**
   * Квалифицированный лид ОДНИМ признаком: побывал на Interested или Park Tour
   * ЛИБО завершился выигранной сделкой. Пары `reached` + `won` не хватало —
   * сервер соединяет их через AND и даёт 74 там, где на плитке 1 250.
   */
  qualified?: boolean;
  /** Причина провала: номер значения «Reason for closure» строкой или `none`. */
  lossReason?: string;
};

/**
 * Утренний дайджест.
 *
 * Сравнение идёт с ТЕМ ЖЕ ДНЁМ НЕДЕЛИ неделю назад, а не со вчера: у детского
 * парка недельная сезонность сильнее любого тренда, и суббота против пятницы
 * даёт «рост» на десятки процентов каждую неделю.
 */
export type DigestReport = {
  day: string;
  prevDay: string;
  sparkDays: number;
  leads: { value: number; prev: number; qualified: number };
  /** Сделки, созданные за сутки, и сколько из них без лида. */
  newDeals: { value: number; prev: number; no_lead: number };
  /**
   * Праздники, ПРОШЕДШИЕ за сутки, и сколько из них уже закрыто в CRM.
   * Считается по дате праздника, а не по дате закрытия сделки: подробности —
   * в самом запросе, `api/digest.ts`.
   */
  won: { value: number; prev: number; closed: number; no_pipeline: number };
  deposits: {
    value: string;
    prev: string;
    payments: number;
    /** Сколько из них закрывают сделку, созданную РАНЬШЕ этого дня. */
    for_older_deal: number;
    /** Сколько платежей вообще не привязаны к сделке. */
    without_deal: number;
  };
  pos: { value: string; prev: string; parks_reporting: number; parks_total: number };
  /** Ряд на 45 дней, дни без данных присутствуют нулями. */
  trend: Array<{
    d: string;
    leads: number;
    qualified: number;
    deals: number;
    won: number;
    deposit_aed_minor: string;
    pos_aed_minor: string;
  }>;
  attention: {
    /** Обращения, на которые никто не ответил и клиент ждёт до сих пор. */
    noReply: Array<{
      session_id: number;
      line_name: string | null;
      owner_type: number;
      owner_id: number;
      park_name: string | null;
      wrote_at: string;
      hours_waiting: number;
      client_messages: number;
      total: number;
    }>;
    ageing: { leads: number; oldest_days: number };
    unknownSpend: {
      aed_minor: string;
      days: number;
      /**
       * Кампании, которые не подошли ни под одно правило парка. До шести.
       * Рядом название кабинета: расход без парка бывает и от того, что парк не
       * задан самому кабинету, и по имени кампании это не отличить.
       */
      campaigns: Array<{
        platform: string;
        name: string | null;
        account_name: string | null;
        aed_minor: string;
      }>;
    };
    staleSources: Array<{
      name: string;
      status: string;
      finished_at: string | null;
      hours_ago: number | null;
      error: string | null;
      /** Сколько источников отстало ВСЕГО: строк приезжает не больше восьми. */
      stale_total: number;
    }>;
    unmapped: { leads: number; tags: number };
  };
  /**
   * Ближайшие праздники числом, а не строками (с 19.10.2026). Окно `from_day..
   * to_day` считает сервер — дубайскими датами, — и плитка отдаёт его списку
   * под числом как есть: дата браузера бывает UTC и уехала бы на день.
   */
  upcoming: {
    from_day: string;
    to_day: string;
    events: number;
    /** Сделки без единой оплаченной брони — срез `hasPaidBooking: false`. */
    without_paid_booking: number;
  };
  queryMs: number;
};

/** Сохранённый набор фильтров. Принадлежит человеку, а не роли. */
export type SavedView = {
  id: string;
  name: string;
  tint: "yellow" | "sky" | "pink" | "violet" | "orange";
  filters: {
    preset?: string;
    from?: string;
    to?: string;
    era?: "managed" | "all";
    park?: string[];
    brand?: string[];
    screen?: string;
    compare?: boolean;
  };
  created_at: string;
};

export type Filters = {
  from: string;
  to: string;
  era?: "managed" | "legacy" | "all";
  park?: string[];
  brand?: string[];
  channel?: string[];
};

/**
 * Предыдущий период ТОЙ ЖЕ ДЛИНЫ, встык к выбранному.
 *
 * Именно той же длины, а не «прошлый месяц»: сравнивать девять дней с тридцатью
 * бессмысленно, а именно так и получилось бы при фиксированном шаге. Встык, а не
 * с зазором — иначе между периодами теряются дни, и сумма двух периодов
 * перестаёт быть суммой.
 *
 * Остальные фильтры (парки, бренды, эра) переносятся без изменений: сравнение
 * должно отличаться от основного среза РОВНО периодом, иначе разница будет
 * говорить не о времени.
 */
export function previousPeriod(f: Filters): Filters {
  const DAY = 86_400_000;
  const from = Date.parse(`${f.from}T00:00:00Z`);
  const to = Date.parse(`${f.to}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return f;

  const lengthDays = Math.round((to - from) / DAY) + 1;
  const prevTo = new Date(from - DAY);
  const prevFrom = new Date(from - lengthDays * DAY);
  const iso = (d: Date): string => d.toISOString().slice(0, 10);
  return { ...f, from: iso(prevFrom), to: iso(prevTo) };
}

function qs(f: Filters): string {
  const p = new URLSearchParams({ from: f.from, to: f.to });
  if (f.era) p.set("era", f.era);
  if (f.park?.length) p.set("park", f.park.join(","));
  if (f.brand?.length) p.set("brand", f.brand.join(","));
  if (f.channel?.length) p.set("channel", f.channel.join(","));
  return p.toString();
}

/**
 * Курс валюты к базовой (AED).
 *
 * `in_process` — курс, по которому считает ЗАГРУЗКА прямо сейчас. Он показан
 * отдельно от `aed_per_unit` не для полноты: до миграции 059 курс жил в двух
 * местах и они могли расходиться. Если разойдутся снова, это будет видно на
 * экране, а не в расследовании причин, почему выручка Бахрейна не сходится.
 */
export type FxRate = {
  currency: string;
  aed_per_unit: string;
  minor_units: number;
  name: string;
  in_process: number | null;
  editable: boolean;
};

export type FxChange = {
  currency: string;
  old_rate: string;
  new_rate: string;
  rows_repriced: number;
  changed_by: string;
  changed_at: string;
};

/**
 * Параметры среза сделок в строку запроса.
 *
 * Один сборщик на список и на его разбивку по месяцам: разойдись они хоть одним
 * полем, и сумма месяцев перестала бы равняться числу на плитке.
 */
function sliceParams(f: Filters, slice: DealSlice): URLSearchParams {
  const p = new URLSearchParams(qs(f));
  for (const [k, v] of Object.entries(slice)) {
    if (v === undefined || v === "") continue;
    p.set(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
  }
  return p;
}

/** Строка списка диалогов открытой линии. */
export type ConversationRow = {
  session_id: number;
  line_name: string | null;
  park_name: string | null;
  wrote_at: string | null;
  /** Только у неотвеченных: у отвеченного «ждёт N часов» — бессмыслица. */
  hours_waiting: number | null;
  answered: boolean;
  first_reply_by: "bot" | "human" | "outgoing" | null;
  reply_minutes: number | null;
  client_messages: number;
  /** Сущность Битрикса, к которой прикреплён диалог, — куда идти отвечать. */
  entity: "lead" | "deal" | "contact" | null;
  entity_id: number | null;
  entity_title: string | null;
  entity_stage: string | null;
  manager_name: string | null;
  link: string | null;
};

export type ConversationPage = {
  total: number;
  rows: ConversationRow[];
  limit: number;
  offset: number;
};

/** Срез диалогов. Окно своё, а не с верхней панели: карточка считает свои дни. */
export type ConversationSlice = {
  /** `undefined` — все, `false` — только оставшиеся без ответа. */
  answered?: boolean;
};

export const api = {
  me: () => request<Me>("/api/me"),
  login: (username: string, password: string) =>
    request<{ token: string }>("/api/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  meta: () => request<Meta>("/api/meta"),
  parksGeo: () => request<{ parks: ParkGeo[] }>("/api/parks/geo"),
  overview: (f: Filters) => request<Overview>(`/api/dashboard/overview?${qs(f)}`),
  parkRevenue: (f: Filters) => request<ParkRevenue>(`/api/dashboard/park-revenue?${qs(f)}`),
  brands: (f: Filters) => request<BrandReport>(`/api/dashboard/brands?${qs(f)}`),
  pipeline: (f: Filters) => request<PipelineReport>(`/api/dashboard/pipeline?${qs(f)}`),
  operations: (f: Filters) => request<OperationsReport>(`/api/dashboard/operations?${qs(f)}`),
  marketing: (f: Filters) => request<MarketingReport>(`/api/dashboard/marketing?${qs(f)}`),
  salesWeekly: (f: Filters) => request<SalesWeekly>(`/api/dashboard/sales-weekly?${qs(f)}`),
  journey: (f: Filters) => request<JourneyReport>(`/api/dashboard/journey?${qs(f)}`),
  /** Поиск человека фильтрами периода НЕ ограничен: человека ищут по имени и
   *  телефону, а не по тому, попал ли он в выбранные даты. */
  journeySearch: (q: string) =>
    request<JourneySearchResult>(`/api/journey/search?q=${encodeURIComponent(q)}`),
  journeyPerson: (key: string) =>
    request<JourneyPerson>(`/api/journey/person?key=${encodeURIComponent(key)}`),
  exportCatalog: () => request<{ datasets: ExportDataset[] }>("/api/export"),

  /**
   * Дайджест берёт только фильтр парков: он про сутки, и период с верхней
   * панели к нему не применяется.
   */
  digest: (park?: string[]) =>
    request<DigestReport>(
      `/api/dashboard/digest${park && park.length > 0 ? `?park=${park.join(",")}` : ""}`,
    ),

  views: () => request<{ views: SavedView[] }>("/api/views"),
  saveView: (body: { name: string; tint: string; filters: Record<string, unknown> }) =>
    request<{ view: SavedView }>("/api/views", { method: "POST", body: JSON.stringify(body) }),
  deleteView: (id: string) =>
    request<{ deleted: boolean }>(`/api/views/${id}`, { method: "DELETE" }),

  /**
   * Список лидов под цифрой. Срез уезжает теми же параметрами, что стоят в
   * условии плитки, поэтому «всего» в списке равно числу на плитке.
   */
  leads: (f: Filters, slice: LeadSlice, limit = 100, offset = 0) => {
    const p = new URLSearchParams(qs(f));
    for (const [k, v] of Object.entries(slice)) {
      if (v === undefined || v === "") continue;
      p.set(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
    }
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<LeadListPage>(`/api/leads?${p.toString()}`);
  },

  /**
   * Список СДЕЛОК под цифрой. Шаг 200, а не 100: сделок в базе 3 299, и по
   * сотне это 33 страницы вместо 17. Потолок сервера — 500.
   */
  deals: (f: Filters, slice: DealSlice, limit = 200, offset = 0) => {
    const p = sliceParams(f, slice);
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<DealListPage>(`/api/deals?${p.toString()}`);
  },
  /**
   * Первая ступень детализации по сделкам: на какие месяцы праздника они легли.
   *
   * Параметры собираются тем же `sliceParams`, что и список, — иначе сумма
   * месяцев однажды перестанет равняться числу, под которым её открыли.
   */
  dealMonths: (f: Filters, slice: DealSlice) =>
    request<DealMonthsPage>(`/api/deals/months?${sliceParams(f, slice).toString()}`),

  /**
   * Список ДИАЛОГОВ под цифрой. Третий вид детализации рядом с лидами и
   * сделками: у карточки «Никто не ответил» объект — диалог, а не запись CRM,
   * и список лидов под ней был бы короче собственного числа.
   */
  conversations: (f: Filters, slice: ConversationSlice, limit = 200, offset = 0) => {
    const p = new URLSearchParams(qs(f));
    if (slice.answered !== undefined) p.set("answered", slice.answered ? "1" : "0");
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<ConversationPage>(`/api/conversations?${p.toString()}`);
  },

  /** Оплаты из блока «Deposits to fix»: на лиде или без сделки. Тот же отбор, что у числа. */
  depositPayments: (f: Filters, kind: "on_lead" | "without_deal", limit = 200, offset = 0) => {
    const p = new URLSearchParams(qs(f));
    p.set("kind", kind);
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<DepositPaymentPage>(`/api/deposit-payments?${p.toString()}`);
  },

  /** Брони сайта билетов под цифрами блока «Онлайн-билеты по источникам». */
  ticketBookings: (f: Filters, slice: TicketSlice, limit = 200, offset = 0) => {
    const p = new URLSearchParams(qs(f));
    p.set("basis", slice.basis);
    if (slice.source) p.set("source", slice.source);
    if (slice.campaign) {
      p.set("campaignPlatform", slice.campaign.platform);
      p.set("campaignId", slice.campaign.id);
    }
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<TicketBookingPage>(`/api/ticket-bookings?${p.toString()}`);
  },
  /** Чеки праздников под плиткой банкетной кассы. Тот же отбор, что у плитки. */
  partyChecks: (f: Filters, slice: { parkKey?: string }, limit = 200, offset = 0) => {
    const p = new URLSearchParams(qs(f));
    if (slice.parkKey) p.set("parkKey", slice.parkKey);
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return request<PartyCheckPage>(`/api/party-checks?${p.toString()}`);
  },
  /**
   * Исключения периода. `affecting` — те, что изменили праздники (их число
   * стоит под плиткой), `all` — все записи, включая ожидающие и исправленные.
   */
  exceptions: (f: Filters, status: "affecting" | "all") => {
    const p = new URLSearchParams(qs(f));
    p.set("status", status);
    return request<ExceptionPage>(`/api/exceptions?${p.toString()}`);
  },

  freshness: () => request<{ sources: SourceHealth[]; pools: PoolStats }>("/api/etl/freshness"),
  running: () => request<{ running: RunningJob[] }>("/api/etl/running"),
  sync: (opts: { full?: boolean; only?: string[] } = {}) =>
    request<{ started: boolean; mode: string; connectors: string[] }>("/api/etl/sync", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  users: () => request<{ users: AppUser[]; screens: AppScreen[] }>("/api/admin/users"),
  openLines: () =>
    request<{
      summary: WatchdogSummary;
      actions: WatchdogAction[];
      run: WatchdogRun | null;
      parkManagers: WatchdogParkManagers;
    }>("/api/admin/open-lines"),
  /** `parkKey: null` — строка «парк не определён», `bitrixUserId: null` — снять. */
  setWatchdogParkManager: (parkKey: string | null, bitrixUserId: number | null) =>
    request<{ parkManagers: WatchdogParkManagers }>("/api/admin/open-lines/park-managers", {
      method: "PUT",
      body: JSON.stringify({ parkKey, bitrixUserId }),
    }),
  settings: () =>
    request<{ settings: AppSetting[]; changes: SettingChange[] }>("/api/admin/settings"),
  cohortPreview: (eventFrom: string, eventTo: string, park?: string[]) =>
    request<CohortPreview>(
      `/api/cohorts/rebooking?eventFrom=${eventFrom}&eventTo=${eventTo}` +
        (park?.length ? `&park=${park.join(",")}` : ""),
    ),
  /** Мутирует боевую CRM: вызывается только по нажатию, после предпросмотра. */
  cohortCreate: (eventFrom: string, eventTo: string, dealIds: string[], park?: string[]) =>
    request<{ runId: number; created: number; skipped: number; failed: number; notified: number }>(
      "/api/cohorts/rebooking",
      { method: "POST", body: JSON.stringify({ eventFrom, eventTo, dealIds, park }) },
    ),
  cohortRuns: () => request<{ runs: CohortRun[] }>("/api/cohorts/runs"),
  cohortAuto: () => request<CohortAutoStatus>("/api/cohorts/auto"),
  /** Рубильник автозапуска: ничего не создаёт сразу, первый запуск — 1-го числа. */
  cohortAutoSet: (enabled: boolean) =>
    request<CohortAutoStatus>("/api/cohorts/auto", { method: "PUT", body: JSON.stringify({ enabled }) }),
  updateSetting: (key: string, value: string) =>
    request<{ setting: AppSetting; changed: boolean; appliesImmediately: boolean }>(
      `/api/admin/settings/${encodeURIComponent(key)}`,
      { method: "PATCH", body: JSON.stringify({ value }) },
    ),
  createUser: (body: Record<string, unknown>) =>
    request<{ user: AppUser }>("/api/admin/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (id: number, body: Record<string, unknown>) =>
    request<{ user: AppUser }>(`/api/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deactivateUser: (id: number) =>
    request<{ deactivated: boolean }>(`/api/admin/users/${id}`, { method: "DELETE" }),
  fx: () => request<{ rates: FxRate[]; changes: FxChange[] }>("/api/fx"),
  /**
   * Меняет курс и пересчитывает уже посчитанное. Отдаёт число пересчитанных
   * строк: правка курса, после которой цифры не сдвинулись, выглядит как
   * поломка, поэтому ответ обязан сказать, что именно произошло.
   */
  setFxRate: (currency: string, rate: string) =>
    request<{ changed: boolean; rate: FxRate; from?: string; rowsRepriced: number }>(
      `/api/fx/${encodeURIComponent(currency)}`,
      { method: "POST", body: JSON.stringify({ rate }) },
    ),
  download: downloadExport,
  exportTable: exportTableXlsx,
};

/** Таблица, как её показывает экран: подписи колонок, тип и уже собранные ячейки. */
export type TableExportPayload = {
  title: string;
  columns: Array<{ title: string; kind?: "text" | "number" | "money" | "date" | "datetime" | "percent"; width?: number }>;
  rows: Array<Array<string | number | null>>;
  /** Тот же список по листам с одинаковыми колонками; тогда `rows` пуст. */
  sheets?: Array<{ name: string; rows: Array<Array<string | number | null>> }>;
};

/**
 * Excel открытого списка. Запрос — тем же путём с заголовком Authorization, что
 * и `downloadExport`, по той же причине (iframe Битрикса режет сторонние cookie).
 */
async function exportTableXlsx(payload: TableExportPayload): Promise<Blob> {
  const token = bootToken();
  const res = await fetch("/api/export/table", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* тело не JSON */
    }
    throw new ApiError(res.status, msg);
  }
  return res.blob();
}

/**
 * Скачивание выгрузки.
 *
 * Обычная ссылка здесь не годится: приложение открывается в iframe Битрикса, где
 * браузер может резать сторонние cookie, и файл вернулся бы как 401. Поэтому
 * запрос идёт тем же путём, что и остальной API — с заголовком Authorization, —
 * а сохранение делается уже из полученного blob.
 */
async function downloadExport(dataset: string, format: "csv" | "xlsx", f: Filters): Promise<void> {
  const token = bootToken();
  const res = await fetch(`/api/export/${dataset}.${format}?${qs(f)}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; hint?: string };
      msg = [body.error, body.hint].filter(Boolean).join(" — ") || msg;
    } catch {
      /* тело не JSON */
    }
    throw new ApiError(res.status, msg);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = named ?? `${dataset}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Освобождаем сразу: браузер уже забрал содержимое в загрузку.
  URL.revokeObjectURL(url);
}
